import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSectionTranslation } from '@/hooks';
import { useLocale } from '@/lib/i18n';
import { type MarketplaceProductCard } from '../useMarketplaceHome';
import { featuredListingsTranslations } from './translations';
import { ProductCard } from '@/features/marketplace/ProductCard';

interface FeaturedListingsProps {
  featured: MarketplaceProductCard[];
  recent: MarketplaceProductCard[];
  loading: boolean;
}

export function FeaturedListings({ featured, recent, loading }: FeaturedListingsProps) {
  const t = useSectionTranslation(featuredListingsTranslations);
  const { direction } = useLocale();
  const isRTL = direction === 'rtl';

  const renderCard = (card: MarketplaceProductCard) => (
    <ProductCard
      key={card.productId}
      productId={card.productId}
      name={card.name}
      image={card.image}
      gameName={card.gameName}
      gameSlug={card.gameSlug}
      listingType={card.listingType}
      deliveryMethod={card.deliveryMethod}
      deliveryMinutes={card.deliveryMinutes}
      warrantyDays={card.warrantyDays}
      sellerName={card.sellerName}
      sellerUsername={card.sellerUsername}
      sellerAvatarUrl={card.sellerAvatarUrl}
      sellerRating={card.sellerRating}
      sellerRatingCount={card.sellerRatingCount}
      sellerVerified={card.sellerVerified}
      sellerCompletedOrders={card.sellerCompletedOrders}
      sellerRank={card.sellerRank}
      sellerOnline={card.sellerOnline}
      unitsSold={card.unitsSold}
      offerCount={card.offerCount}
      lowestPrice={card.price}
      currency={card.currency}
    />
  );

  const isEmpty = featured.length === 0 && recent.length === 0;

  return (
    <section className="py-16 md:py-24" id="featured-listings">
      <div className="container-main">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-accent mb-3 block">
              {t.section.eyebrow}
            </span>
            <h2 className="font-display text-display-md text-brand-white mb-3">
              {t.section.headline}
            </h2>
            <p className="text-body-md text-brand-muted max-w-xl">
              {t.section.subtext}
            </p>
          </div>

          <div className="flex flex-wrap gap-3 shrink-0">
            <Link to="/browse" className="btn-primary h-11">
              {t.cta.primary}
              <ArrowRight
                size={16}
                style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
              />
            </Link>
            <Link to="/seller" className="btn-secondary h-11">
              {t.cta.secondary}
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" aria-hidden="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-72 rounded-2xl bg-surface-card border border-surface-border animate-pulse"
              />
            ))}
          </div>
        ) : isEmpty ? (
          <p className="text-sm text-brand-muted py-10 text-center rounded-2xl bg-surface-card/40 border border-surface-border">
            {t.empty}
          </p>
        ) : (
          <>
            {featured.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {featured.map(renderCard)}
              </div>
            )}

            {recent.length > 0 && (
              <div className={featured.length > 0 ? 'mt-14' : undefined}>
                <h3 className="font-display text-2xl text-brand-white mb-6">
                  {t.recent.title}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {recent.map(renderCard)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
